/*
Bottom Navigation Component - Cedar Brand v2.0
Custom icons matching Coach Cedar branding
*/

import PropTypes from "prop-types";
import React from "react";
import "./style.css";
import { ChatWithCedarIcon } from "../../../../../components/ChatWithCedarIcon";

// Teal when hover or pressed; cedar dark when selected (filled); gray when inactive
const navIconColor = (active, hover, pressed) => {
  const useTeal = hover || pressed;
  return {
    stroke: useTeal ? "#42A5A5" : (active ? "#1f6c4f" : "#6b7a6e"),
    fill: useTeal ? "#42A5A5" : (active ? "#1f6c4f" : "none"),
  };
};

// Move Icon - Lightning bolt (energy)
const MoveIcon = ({ active, hover, pressed }) => {
  const { stroke: strokeColor, fill: fillColor } = navIconColor(active, hover, pressed);
  
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Lightning bolt silhouette */}
      <path
        /* Flat-top bolt */
        d="M7 2H14L12 10H19L9 22L11 14H5L7 2Z"
        stroke={strokeColor}
        fill={fillColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Insights Icon - Lightbulb/idea
const InsightsIcon = ({ active, hover, pressed }) => {
  const { stroke: strokeColor, fill: fillColor } = navIconColor(active, hover, pressed);
  
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Lucide lightbulb (https://lucide.dev/icons/lightbulb) */}
      <path
        d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
      <path
        d="M9 18h6"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 22h4"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Account Icon - User/profile (replaces Settings)
const AccountIcon = ({ active, hover, pressed }) => {
  const { stroke: strokeColor, fill: fillColor } = navIconColor(active, hover, pressed);

  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Account (user/profile) icon — matches the nav icon styling */}
      <circle
        cx="12"
        cy="8"
        r="3"
        stroke={strokeColor}
        strokeWidth="1.5"
        fill={fillColor}
      />
      <path
        // Filled silhouette on active/hover, outline-only when inactive
        d="M5 20c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5H5Z"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={fillColor}
      />
    </svg>
  );
};

export const CmpBootomnavCoach = ({ activeTab, className, onClickTab = () => {} }) => {
  const [hoveredTab, setHoveredTab] = React.useState(null);
  const [pressedTab, setPressedTab] = React.useState(null);

  const handlePointer = (tab, isDown) => {
    if (isDown) setPressedTab(tab);
    else setPressedTab(null);
  };

  return (
    <div className={`cmp-bootomnav-coach ${className}`}>
      <div 
        className={`nav-frame ${activeTab === "cedar" ? "active" : ""}`}
        data-tab="cedar"
        onClick={() => onClickTab('cedar')}
        onMouseEnter={() => setHoveredTab('cedar')}
        onMouseLeave={() => { setHoveredTab(null); setPressedTab(null); }}
        onMouseDown={() => handlePointer('cedar', true)}
        onMouseUp={() => handlePointer('cedar', false)}
      >
        {activeTab === "cedar" && <div className="active-indicator" />}
        <div className="nav-icon">
          <ChatWithCedarIcon
            variant="nav"
            size="medium"
            active={activeTab === "cedar"}
            hover={hoveredTab === "cedar"}
            pressed={pressedTab === "cedar"}
          />
        </div>
        <div className={`nav-label ${activeTab === "cedar" ? "active" : ""}`}>Cedar</div>
      </div>

      <div 
        className={`nav-frame ${activeTab === "mypath" ? "active" : ""}`}
        data-tab="mypath"
        onClick={() => onClickTab('mypath')}
        onMouseEnter={() => setHoveredTab('mypath')}
        onMouseLeave={() => { setHoveredTab(null); setPressedTab(null); }}
        onMouseDown={() => handlePointer('mypath', true)}
        onMouseUp={() => handlePointer('mypath', false)}
      >
        {activeTab === "mypath" && <div className="active-indicator" />}
        <div className="nav-icon">
          <MoveIcon active={activeTab === "mypath"} hover={hoveredTab === 'mypath'} pressed={pressedTab === 'mypath'} />
        </div>
        <div className={`nav-label ${activeTab === "mypath" ? "active" : ""}`}>Plan</div>
      </div>

      <div 
        className={`nav-frame ${activeTab === "insights" ? "active" : ""}`}
        data-tab="insights"
        onClick={() => onClickTab('insights')}
        onMouseEnter={() => setHoveredTab('insights')}
        onMouseLeave={() => { setHoveredTab(null); setPressedTab(null); }}
        onMouseDown={() => handlePointer('insights', true)}
        onMouseUp={() => handlePointer('insights', false)}
      >
        {activeTab === "insights" && <div className="active-indicator" />}
        <div className="nav-icon">
          <InsightsIcon active={activeTab === "insights"} hover={hoveredTab === 'insights'} pressed={pressedTab === 'insights'} />
        </div>
        <div className={`nav-label ${activeTab === "insights" ? "active" : ""}`}>Insights</div>
      </div>

      <div 
        className={`nav-frame ${activeTab === "settings" ? "active" : ""}`}
        data-tab="settings"
        onClick={() => onClickTab('settings')}
        onMouseEnter={() => setHoveredTab('settings')}
        onMouseLeave={() => { setHoveredTab(null); setPressedTab(null); }}
        onMouseDown={() => handlePointer('settings', true)}
        onMouseUp={() => handlePointer('settings', false)}
      >
        {activeTab === "settings" && <div className="active-indicator" />}
        <div className="nav-icon">
          <AccountIcon active={activeTab === "settings"} hover={hoveredTab === 'settings'} pressed={pressedTab === 'settings'} />
        </div>
        <div className={`nav-label ${activeTab === "settings" ? "active" : ""}`}>Account</div>
      </div>
    </div>
  );
};

CmpBootomnavCoach.propTypes = {
  activeTab: PropTypes.oneOf(["mypath", "insights", "settings", "cedar"]),
  onClickTab: PropTypes.func,
};
